import { cva, type VariantProps } from 'class-variance-authority';

import {
  CheckCircleIcon,
  CloseIcon,
  ErrorIcon,
  InfoIcon,
  WarningIcon,
} from '@/components/icons/registry';
import type { IconComponent } from '@/components/icons/types';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { cn } from '@/utils/helpers/cn';

import type { ReactNode } from 'react';

/**
 * Bloco de aviso dentro do conteúdo — não confundir com toast, que é
 * transitório e vive fora do fluxo.
 *
 * Os pares fundo/texto saem dos tokens `*-subtle` / `*-subtle-foreground`, que
 * já foram medidos em WCAG AA (docs/apendice-tema-arcus.md). Montar a cor à mão
 * aqui refaria a conta errado.
 */
export const calloutVariants = cva('flex gap-3 rounded-xl border p-4', {
  variants: {
    variant: {
      default: 'border-border bg-surface-subtle text-foreground',
      info: 'border-info-subtle bg-info-subtle text-info-subtle-foreground',
      success: 'border-success-subtle bg-success-subtle text-success-subtle-foreground',
      warning: 'border-warning-subtle bg-warning-subtle text-warning-subtle-foreground',
      error: 'border-destructive-subtle bg-destructive-subtle text-destructive-subtle-foreground',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type CalloutVariant = NonNullable<VariantProps<typeof calloutVariants>['variant']>;

const DEFAULT_ICONS: Record<CalloutVariant, IconComponent | null> = {
  // `default` não tem ícone: é o bloco neutro, e um pictograma genérico ali só
  // ocuparia espaço sem acrescentar sentido.
  default: null,
  info: InfoIcon,
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: ErrorIcon,
};

interface CalloutProps {
  variant?: CalloutVariant;
  /** `false` remove o ícone; omitido usa o default da variante. */
  icon?: ReactNode | false;
  title?: string;
  children?: ReactNode;
  onDismiss?: () => void;
  className?: string;
}

export function Callout({
  variant = 'default',
  icon,
  title,
  children,
  onDismiss,
  className,
}: CalloutProps) {
  const defaultIcon = DEFAULT_ICONS[variant];
  // `false` desliga; `undefined` cai no default da variante; qualquer outro nó
  // é usado como veio. Um `??` sozinho não distinguiria `false` de ausente.
  const renderedIcon =
    icon === false ? null : (icon ?? (defaultIcon ? <Icon as={defaultIcon} size="md" /> : null));

  return (
    <div className={cn(calloutVariants({ variant }), className)}>
      {renderedIcon}

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {title ? (
          <Text variant="title" as="p">
            {title}
          </Text>
        ) : null}
        {children ? <Text variant="body">{children}</Text> : null}
      </div>

      {/*
       * ⚠️ A dispensa **não** é controlada aqui: o botão só chama `onDismiss`.
       * Um Callout que se esconde sozinho não pode ser reaberto pelo pai e
       * quebra qualquer fluxo que precise reexibi-lo — quem decide se some, se
       * a escolha persiste e se volta é o consumidor (docs/03).
       */}
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dispensar aviso"
          className="-my-1 -mr-2 grid size-11 shrink-0 place-items-center"
        >
          <Icon as={CloseIcon} size="md" />
        </button>
      ) : null}
    </div>
  );
}
